import React, { useEffect, useState } from 'react';
import httpService from '../../services/httpService';
import { useAuthStore, useChatStore } from '../../store/store';
import ChatList from './ChatList';
import ChatThread from './ChatThread';

/**
 * PodcastClubChat — top-level container for 1-to-1 club chat.
 *
 * Layout branches on user type:
 *
 *   Podcaster  → two-column: ChatList (sidebar) + ChatThread (active thread)
 *   Listener   → single column: ChatThread directly
 *
 * ChatThread only needs clubGuid + listenerGuid. If the thread doesn't exist
 * the backend creates it on the first message/request.
 *
 * Props:
 *   clubGuid   string  — the podcast club's GUID
 *   className  string  — optional extra CSS class
 */
export default function PodcastClubChat({ clubGuid, className = '' }) {
    const [userType, setUserType] = useState(null);
    const [clubTitle, setClubTitle] = useState('');
    const [initError, setInitError] = useState(null);
    // Listener's own listener_guid (for passing to ChatThread)
    const [myListenerGuid, setMyListenerGuid] = useState(null);
    // Mobile: show thread panel over list
    const [mobileShowThread, setMobileShowThread] = useState(false);

    const chats = useChatStore((s) => s.chats);
    const activeListenerGuid = useChatStore((s) => s.activeChatGuid); // reusing field as listenerGuid key
    const { setChats, setActiveChatGuid, reset } = useChatStore.getState();

    // -----------------------------------------------------------------------
    // Initialise
    // -----------------------------------------------------------------------
    useEffect(() => {
        reset();
        setInitError(null);
        setUserType(null);
        setMyListenerGuid(null);

        const init = async () => {
            try {
                const res = await httpService.get(`chat/club/${clubGuid}/chats`);
                const body = res.data;
                setUserType(body.user_type);
                setClubTitle(body.club_title ?? '');

                if (body.user_type === 'podcaster') {
                    setChats(body.data ?? []);
                } else {
                    // listener_guid is the listener's own listeners.guid
                    setMyListenerGuid(body.listener_guid ?? null);
                    if (body.data) {
                        setChats([body.data]);
                    }
                    // Auto-select their own thread so ChatThread loads immediately
                    if (body.listener_guid) {
                        setActiveChatGuid(body.listener_guid);
                    }
                }
            } catch {
                setInitError('Could not load chat. Please try again.');
            }
        };

        init();
        return reset;
    }, [clubGuid]);

    // -----------------------------------------------------------------------
    // Podcaster selects a conversation from the sidebar
    // -----------------------------------------------------------------------
    const handleSelectChat = (chat) => {
        setActiveChatGuid(chat.listener_guid);
        setMobileShowThread(true);
    };

    const activeChat = chats.find((c) => c.listener_guid === activeListenerGuid) ?? null;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    if (initError) {
        return (
            <div className={`podcast-club-chat podcast-club-chat--error ${className}`}>
                <p>{initError}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    if (!userType) {
        return (
            <div className={`podcast-club-chat podcast-club-chat--loading ${className}`}>
                <div className="podcast-club-chat__spinner" />
            </div>
        );
    }

    // ---- Listener view — single thread -----------------------------------
    if (userType === 'listener') {
        if (!myListenerGuid) {
            return (
                <div className={`podcast-club-chat podcast-club-chat--no-thread ${className}`}>
                    <small className={'text-muted'}>A direct line to the host, just you and them. Not a group chat, not a public thread.</small>
                    <p className="podcast-club-chat__notice">
                        Your conversation with the podcaster hasn't started yet.
                        They'll reach out to you here.
                    </p>
                </div>
            );
        }

        return (
            <>
                <div className={'mb-3'}>
                    <small className={'text-muted'}>A direct line to the host, just you and them. Not a group chat, not a public thread.</small>
                </div>
                <div className={`podcast-club-chat podcast-club-chat--listener ${className}`}>
                    <ChatThread
                        clubGuid={clubGuid}
                        listenerGuid={myListenerGuid}
                        participant={{ first_name: 'Podcaster' }}
                        userType="listener"
                    />
                </div>
            </>
        );
    }

    // ---- Podcaster view — sidebar + thread --------------------------------
    return (
        <div className={`podcast-club-chat podcast-club-chat--podcaster ${className}`}>
            <div className={`podcast-club-chat__sidebar ${mobileShowThread ? 'podcast-club-chat__sidebar--hidden' : ''}`}>
                <ChatList
                    chats={chats}
                    activeChatGuid={activeListenerGuid}
                    onSelect={handleSelectChat}
                    clubTitle={clubTitle}
                />
            </div>

            <div className={`podcast-club-chat__thread-pane ${!activeListenerGuid ? 'podcast-club-chat__thread-pane--empty' : ''} ${mobileShowThread ? 'podcast-club-chat__thread-pane--mobile-visible' : ''}`}>
                {activeListenerGuid && activeChat ? (
                    <ChatThread
                        key={activeListenerGuid}
                        clubGuid={clubGuid}
                        listenerGuid={activeListenerGuid}
                        participant={activeChat.listener}
                        userType="podcaster"
                        onBack={() => setMobileShowThread(false)}
                    />
                ) : (
                    <div className="podcast-club-chat__empty-thread">
                        <EmptyThreadIcon />
                        <p>Select a conversation to start messaging</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function EmptyThreadIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dee2e6" strokeWidth="1.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );
}
